import { Edit } from 'lucide-react';
import { KeyboardEvent, useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { updateOrg } from '@/services/org/update-org';
import { useNotification } from '@/components/notifications';
import { useOrgStore } from '@/stores/org-store';
import { Card, CardContent } from '@/components/ui/card';
import { LoadingSpinner } from '@/components/loading-spinner';

export const OrgName = () => {
  const { currentOrg, updateOrg: updateOrgInStore } = useOrgStore();
  const [name, setName] = useState<string>(currentOrg?.name || '');
  const notification = useNotification();
  const [isEditMode, setIsEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setName(currentOrg?.name || '');
  }, [currentOrg]);

  const handleSaveName = async () => {
    const trimmedName = name.trim();

    if (!trimmedName || !currentOrg?.orgid) {
      return;
    }

    if (trimmedName.length > 120) {
      notification.error('Organization name cannot exceed 120 characters');
      return;
    }

    setIsLoading(true);
    try {
      const result = await updateOrg({ name: trimmedName });

      if (result?.detail) {
        notification.error(result.detail);
        return;
      }

      updateOrgInStore(currentOrg.orgid, {
        name: trimmedName,
      });

      notification.success('Organization was renamed successfully');
    } catch (error) {
      console.error('Update organization name error:', error);
      notification.error('An unexpected error occurred');
    } finally {
      setIsEditMode(false);
      setIsLoading(false);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleSaveName();
    }
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setName(currentOrg?.name || '');
  };

  return (
    <Card>
      <CardContent className='flex flex-col gap-4'>
        <div className='flex justify-between w-full'>
          <div
            id='org-settings-name-label'
            className='text-zinc-900 dark:text-zinc-50'
          >
            Organization name
          </div>
          {!isEditMode && (
            <Button
              id='org-settings-name-edit'
              onClick={() => setIsEditMode(true)}
              variant='ghost'
              size='icon'
            >
              <Edit />
            </Button>
          )}
        </div>
        {isEditMode ? (
          <div className='flex justify-between w-full gap-5'>
            <Input
              onChange={(e) => {
                setName(e.target.value);
              }}
              onKeyDown={handleKeyDown}
              value={name}
            />
            <div className='flex gap-1.5'>
              <Button
                id='org-settings-name-save'
                onClick={handleSaveName}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <LoadingSpinner /> Saving...
                  </>
                ) : (
                  'Save'
                )}
              </Button>
              <Button
                id='org-settings-name-cancel'
                disabled={isLoading}
                onClick={handleCancel}
                variant='ghost'
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className='flex justify-between w-full font-semibold'>
            {name}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
